import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';

const useSettings = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch(`/api/settings/header`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(err => console.error('Error fetching header announcements:', err));
  }, []);

  return settings;
};

export const TopAnnouncementBar = () => {
  const settings = useSettings();
  if (!settings || !settings.topBar || !settings.topBar.enabled) return null;

  const { topBar } = settings;

  return (
    <div 
      className="w-full min-h-10 flex items-center overflow-hidden relative z-[60] px-3 py-2 sm:px-4" 
      style={{ backgroundColor: topBar.bgColor, color: topBar.textColor }}
    >
      {topBar.isScrolling ? (
        <motion.div
          animate={{ x: ["50%", "-100%"] }}
          transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
          className="whitespace-nowrap flex items-center gap-8 sm:gap-12 px-1 sm:px-4 shrink-0"
        >
          {[...Array(3)].map((_, i) => (
            <span key={i} className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] sm:tracking-wider">
              <Zap size={14} fill="currentColor" />
              {topBar.text}
              <a href={topBar.link} className="underline decoration-white/40 hover:decoration-white font-extrabold ml-2">
                {topBar.cta}
              </a>
            </span>
          ))}
        </motion.div>
      ) : (
        <div className="w-full px-1 sm:px-4 text-center">
          <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] sm:tracking-wider leading-tight flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <Zap size={14} fill="currentColor" />
            {topBar.text}
            <a href={topBar.link} className="underline decoration-white/40 hover:decoration-white font-extrabold ml-2">
              {topBar.cta}
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export const SubAnnouncementBar = () => {
  const settings = useSettings();
  if (!settings || !settings.subBar || !settings.subBar.enabled) return null;

  const { subBar } = settings;

  return (
    <div 
      className="w-full min-h-9 flex items-center overflow-hidden relative z-[59] border-b border-black/5 px-3 py-2 sm:px-4" 
      style={{ backgroundColor: subBar.bgColor, color: subBar.textColor }}
    >
      {subBar.isScrolling ? (
        <motion.div
          animate={{ x: ["50%", "-100%"] }}
          transition={{ repeat: Infinity, duration: 25, ease: "linear" }}
          className="whitespace-nowrap px-1 sm:px-4 flex items-center gap-10 sm:gap-20"
        >
          {[...Array(3)].map((_, i) => (
            <span key={i} className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] sm:tracking-widest">
              {subBar.text}
              {subBar.cta && (
                <a href={subBar.link} className="ml-3 underline font-extrabold">
                  {subBar.cta}
                </a>
              )}
            </span>
          ))}
        </motion.div>
      ) : (
        <div className="w-full px-1 sm:px-4 text-center">
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] sm:tracking-widest leading-tight flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            {subBar.text}
            {subBar.cta && (
              <a href={subBar.link} className="ml-3 underline font-extrabold">
                {subBar.cta}
              </a>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// Default export for backward compatibility or direct use
const AnnouncementBar = () => (
  <>
    <TopAnnouncementBar />
    <SubAnnouncementBar />
  </>
);

export default AnnouncementBar;
